import { useState, useEffect, useCallback, useRef } from 'react';
import { generateClient } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  getMessages as getMessagesQuery,
  sendMessage as sendMessageMutation,
  addReaction as addReactionMutation,
  removeReaction as removeReactionMutation,
  markAsRead as markAsReadMutation,
  onNewMessage as onNewMessageSubscription,
  Message,
  MessageConnection,
} from '../graphql/chat';

// Helper to get client (lazy initialization to ensure Amplify is configured)
const getClient = () => generateClient();

export function useChat(leagueId: string, userName?: string) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const subscriptionRef = useRef<any>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const session = await fetchAuthSession();
      if (!session.tokens) {
        throw new Error('Not authenticated');
      }

      const result: any = await getClient().graphql({
        query: getMessagesQuery,
        variables: { leagueId, limit: 50 },
        authMode: 'userPool',
      });

      const messageData: MessageConnection = result.data.getMessages;
      setMessages(messageData.items || []);
    } catch (err: any) {
      console.error('Failed to fetch messages:', err);
      console.error('Error details:', JSON.stringify(err, null, 2));

      // Better error message
      let errorMsg = 'Failed to load messages';
      if (err.errors && err.errors[0]) {
        errorMsg = err.errors[0].message;
      } else if (err.message) {
        errorMsg = err.message;
      }

      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  // Send message
  const sendMessage = useCallback(
    async (content: string, type: 'USER' | 'SYSTEM' = 'USER') => {
      if (!content.trim()) return;

      try {
        setSending(true);
        console.log('Sending message:', content);
        console.log('Using userName from context:', userName);

        const session = await fetchAuthSession();
        if (!session.tokens) {
          throw new Error('Not authenticated');
        }

        const result: any = await getClient().graphql({
          query: sendMessageMutation,
          variables: {
            leagueId,
            content: content.trim(),
            type,
            userName,
          },
          authMode: 'userPool',
        });

        console.log('Message sent, result:', result);

        // Optimistically add message to UI immediately
        const newMessage: Message = result.data.sendMessage;
        console.log('Adding message to UI:', newMessage);

        setMessages((prev) => {
          // Avoid duplicates
          if (prev.some((m) => m.id === newMessage.id)) {
            console.log('Message already exists, skipping');
            return prev;
          }
          console.log('Adding new message to list');
          return [...prev, newMessage];
        });

        // Scroll to bottom after adding message
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } catch (err) {
        console.error('Failed to send message:', err);
        console.error('Error details:', JSON.stringify(err, null, 2));
        throw err;
      } finally {
        setSending(false);
      }
    },
    [leagueId, userName]
  );

  // Add reaction
  const addReaction = useCallback(
    async (messageId: string, emoji: string) => {
      try {
        const session = await fetchAuthSession();
        if (!session.tokens) {
          throw new Error('Not authenticated');
        }

        await getClient().graphql({
          query: addReactionMutation,
          variables: { messageId, leagueId, emoji },
          authMode: 'userPool',
        });
      } catch (err) {
        console.error('Failed to add reaction:', err);
        throw err;
      }
    },
    [leagueId]
  );

  // Remove reaction
  const removeReaction = useCallback(
    async (messageId: string, emoji: string) => {
      try {
        const session = await fetchAuthSession();
        if (!session.tokens) {
          throw new Error('Not authenticated');
        }

        await getClient().graphql({
          query: removeReactionMutation,
          variables: { messageId, leagueId, emoji },
          authMode: 'userPool',
        });
      } catch (err) {
        console.error('Failed to remove reaction:', err);
        throw err;
      }
    },
    [leagueId]
  );

  // Mark as read
  const markAsRead = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      if (!session.tokens) {
        console.log('[useChat] markAsRead: No session tokens');
        return;
      }

      console.log('[useChat] Marking league as read:', leagueId);
      const result = await getClient().graphql({
        query: markAsReadMutation,
        variables: { leagueId },
        authMode: 'userPool',
      });
      console.log('[useChat] Mark as read result:', result);
    } catch (err) {
      console.error('[useChat] Failed to mark as read:', err);
      console.error('[useChat] Mark as read error details:', JSON.stringify(err, null, 2));
    }
  }, [leagueId]);

  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Subscribe to new messages
  useEffect(() => {
    fetchMessages();

    // Set up real-time subscription
    const setupSubscription = async () => {
      try {
        const session = await fetchAuthSession();
        if (!session.tokens) return;

        const sub = getClient().graphql({
          query: onNewMessageSubscription,
          variables: { leagueId },
          authMode: 'userPool',
        });

        // Check if it's a subscription
        if ('subscribe' in sub) {
          const subscription = (sub as any).subscribe({
            next: ({ data }: any) => {
              const newMessage: Message = data.onNewMessage;
              setMessages((prev) => {
                // Avoid duplicates
                if (prev.some((m) => m.id === newMessage.id)) {
                  return prev;
                }
                return [...prev, newMessage];
              });
              setTimeout(scrollToBottom, 100);
            },
            error: (err: any) => {
              console.error('Subscription error:', err);
            },
          });

          subscriptionRef.current = subscription;
        }
      } catch (err) {
        console.error('Failed to set up subscription:', err);
      }
    };

    setupSubscription();

    // Cleanup
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
    };
  }, [leagueId, fetchMessages, scrollToBottom]);

  return {
    messages,
    loading,
    error,
    sending,
    sendMessage,
    addReaction,
    removeReaction,
    markAsRead,
    messagesEndRef,
    scrollToBottom,
  };
}
